import { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const faqs = [
    {
      question: "How much does a website cost in the Hamptons?",
      answer: "My Hamptons web design packages start at $1,000 for landing pages and go up to $5,000 for custom builds. Unlike NYC agencies that charge $10,000+, I offer premium quality at affordable small business prices with transparent, flat-rate pricing and no hidden fees."
    },
    {
      question: "How long does it take to build a custom website?",
      answer: "Most websites I build are completed within 1-3 weeks depending on the complexity. Simple landing pages can be done in as little as 1 day, while more complex sites with custom features may take 1-2 weeks. My fast turnaround beats typical agency timelines of 2-6 months."
    },
    {
      question: "Do you offer monthly website maintenance?",
      answer: "Yes! My Peace of Mind Management plan includes premium hosting, SSL security, daily backups, and content updates for $75/month or $600/year (saves $300). You can also choose self-managed hosting (~$20/mo) or free static hosting if you prefer."
    },
    {
      question: "Can I update the website myself?",
      answer: "Absolutely! If you choose the self-managed option, you'll have access to an AI-powered editor where you can click, type, and update text instantly. It's the easiest website builder for small business owners. I also provide training and support."
    },
    {
      question: "Do I own my domain and website code?",
      answer: "Yes. You should always buy your own domain so you retain full ownership of your business name. Unlike other agencies that lock you in, I ensure you have full access to your code and can host anywhere—your website is truly yours forever."
    },
    {
      question: "Will my website be mobile-friendly and SEO optimized?",
      answer: "Yes! Every website I build is fully responsive, mobile-optimized, and includes basic SEO setup with semantic HTML, meta tags, fast loading, and clean URLs. I specialize in local SEO for Hamptons businesses to help you rank in local searches."
    },
    {
      question: "What areas do you serve?",
      answer: "I'm proudly based in the Hamptons and serve Southampton, East Hampton, Montauk, Sag Harbor, Bridgehampton, the North Fork, and the entire NYC Metro Area. Being local means I understand your market and competition."
    }
  ];

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="py-12 sm:py-16 bg-gradient-to-b from-background to-secondary/20 relative overflow-hidden"
    >
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10">
          <span
            className={`inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Got Questions?
          </span>
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Frequently Asked Questions About{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              Web Design
            </span>
          </h2>
          <p
            className={`text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Everything Hamptons business owners need to know about custom website design
          </p>
        </div>

        <div
          className={`max-w-3xl mx-auto transition-all duration-700 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-left hover:no-underline py-5 text-base sm:text-lg font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
